

import React, { useState, useEffect } from 'react';
import { PageView } from './types';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import Workspace from './components/Workspace';
import Pricing from './components/Pricing';
import UpgradePage from './components/UpgradePage';
import LoginModal from './components/LoginModal';
import SignUpModal from './components/SignUpModal';
import InviteModal from './components/InviteModal';
import NewRecordingModal from './components/NewRecordingModal';
import ShareModal from './components/ShareModal';
import PublicShareView from './components/PublicShareView';
import GuestGateModal from './components/GuestGateModal';
import WelcomePricingModal from './components/WelcomePricingModal';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import EnterprisePage from './components/EnterprisePage';
import ApiPage from './components/ApiPage';
import AboutPage from './components/AboutPage';
import BlogPage from './components/BlogPage';
import CareersPage from './components/CareersPage';
import ContactPage from './components/ContactPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import SecurityPage from './components/SecurityPage';
import ChangelogPage from './components/ChangelogPage';
import AlertModal from './components/AlertModal';
import { onKickOut, resetKickOutFlag, startHeartbeat, stopHeartbeat } from './services/api';

import { Menu, Globe, Monitor } from 'lucide-react';
import { LanguageProvider, useLanguage } from './i18nContext';

function AppContent() {
  // 根据 token 决定初始页面
  const getInitialPage = (): PageView => {
    const token = localStorage.getItem('token');
    return token ? 'dashboard' : 'landing';
  };

  const [currentPage, setCurrentPage] = useState<PageView>(() => getInitialPage());
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  // 页面加载时检查 token 状态，确保刷新后正确跳转
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && currentPage === 'landing') {
      setCurrentPage('dashboard');
    } else if (!token && currentPage === 'dashboard') {
      setCurrentPage('landing');
    }
  }, []);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isNewRecordingOpen, setIsNewRecordingOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  // New state for the registration gate
  const [isGuestGateOpen, setIsGuestGateOpen] = useState(false);

  // Welcome pricing modal after signup
  const [showWelcomePricing, setShowWelcomePricing] = useState(false);

  // 升级会员弹窗
  const [showUpgrade, setShowUpgrade] = useState(false);

  // 踢出弹窗状态
  const [kickOutReason, setKickOutReason] = useState<'other_device' | 'session_expired' | null>(null);

  // 通用提示弹窗状态
  const [alertModal, setAlertModal] = useState<{ title: string; message: string; confirmText?: string; onConfirm?: () => void } | null>(null);

  // 从升级页返回后是否重新打开新建任务弹窗
  const [returnToNewRecording, setReturnToNewRecording] = useState(false);

  // 当前选中的任务ID
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Dashboard 刷新 key
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  // 邀请码（从URL参数获取）
  const [inviteCode, setInviteCode] = useState<string>('');

  // 登录跳注册时的预填数据
  const [signUpPrefill, setSignUpPrefill] = useState<{ method: 'email' | 'phone'; contact: string; code: string } | null>(null);

  const { t, language, setLanguage } = useLanguage();

  // 检查URL参数中的邀请码
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('inviteCode');
    if (code) {
      setInviteCode(code);
      // 自动打开注册弹窗
      setIsSignUpOpen(true);
      // 清除URL参数，避免刷新后重复触发
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // 监听全局踢出事件 + 心跳
  useEffect(() => {
    const unsubscribe = onKickOut((reason) => {
      setKickOutReason(reason);
      stopHeartbeat();
    });
    // 有 token 时启动心跳
    const token = localStorage.getItem('token');
    if (token) {
      startHeartbeat();
    }
    return () => {
      unsubscribe();
      stopHeartbeat();
    };
  }, []);

  // Navigation Helper
  const navigateTo = (page: PageView) => {
    if (page === 'dashboard') {
      setDashboardRefreshKey(k => k + 1);
    }
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'zh' : 'en');
  };

  // Flow: Start Free Trial -> Dashboard with Gate
  const handleStartFreeTrial = () => {
    navigateTo('dashboard');
    setIsGuestGateOpen(true);
  };

  // Flow: Sign Up Success -> Dashboard
  const handleSignUpSuccess = () => {
    setIsSignUpOpen(false);
    setIsGuestGateOpen(false); // Close the gate if it was open
    navigateTo('dashboard');
    setShowWelcomePricing(true);
    startHeartbeat();
  };

  return (
    <div className="min-h-screen bg-surface text-brand-900 font-sans selection:bg-surface-border selection:text-brand-800">

      {/* Global Navigation (Floating Pill Style) */}
      {(currentPage === 'landing' || currentPage === 'pricing') && (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-6 px-4 pointer-events-none">
          <nav className="pointer-events-auto w-full max-w-6xl bg-surface-light/90 backdrop-blur-xl border border-surface-border rounded-full shadow-nav px-2 pr-2 py-2 relative flex items-center justify-between transition-all duration-300">

            {/* Logo Section - Left (Flex-1) */}
            <div className="flex-1 flex justify-start pl-4">
              <div
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => navigateTo('landing')}
              >
                <div className="w-8 h-8 bg-brand-900 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm font-serif">M</div>
                <span className="text-lg font-bold tracking-tight text-brand-900">Minutes AI</span>
              </div>
            </div>

            {/* Desktop Menu - Centered Pill (Absolute Positioning for True Center) */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center gap-1 bg-surface/50 p-1 rounded-full border border-surface-border">
              <button onClick={() => navigateTo('landing')} className="px-5 py-2 rounded-full text-sm font-medium text-brand-700 hover:text-brand-900 hover:bg-white transition-all shadow-sm hover:shadow-md border border-transparent hover:border-surface-border">{t.nav.features}</button>
              <button onClick={() => navigateTo('pricing')} className="px-5 py-2 rounded-full text-sm font-medium text-brand-700 hover:text-brand-900 hover:bg-white transition-all shadow-sm hover:shadow-md border border-transparent hover:border-surface-border">{t.nav.pricing}</button>
              <button className="px-5 py-2 rounded-full text-sm font-medium text-brand-700 hover:text-brand-900 hover:bg-white transition-all shadow-sm hover:shadow-md border border-transparent hover:border-surface-border">{t.nav.resources}</button>
            </div>

            {/* Right Actions - Right (Flex-1) */}
            <div className="flex-1 flex justify-end items-center gap-2 pr-1">
              <div className="hidden md:flex items-center gap-2">
                {/* Language */}
                <button
                  onClick={toggleLanguage}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-surface text-brand-700 hover:text-brand-900 transition-colors"
                >
                  <Globe size={18} />
                </button>

                <div className="w-px h-4 bg-surface-divider mx-1"></div>

                {/* Log In Button - UPDATED STYLE to match 'All Recordings' header (Serif, Dark Green, Bold) */}
                <button
                  onClick={() => setIsLoginOpen(true)}
                  className="px-5 py-2.5 text-sm font-serif font-bold text-brand-900 hover:text-brand-700 transition-colors"
                >
                  {t.nav.login}
                </button>

                {/* Deep Green Button to match screenshot */}
                <button
                  onClick={() => setIsSignUpOpen(true)}
                  className="relative overflow-hidden bg-brand-900 hover:bg-brand-800 text-white px-6 py-2.5 rounded-full text-sm font-bold shadow-cta-dark hover:shadow-[0_6px_20px_rgba(26,46,5,0.23)] hover:-translate-y-0.5 transition-all duration-200 group"
                >
                  <span className="relative z-10">{t.nav.signup}</span>
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button className="md:hidden p-3 mr-2 rounded-full hover:bg-gray-100">
                <Menu className="w-6 h-6" />
              </button>
            </div>

          </nav>
        </div>
      )}

      {/* Main Content Router */}
      <main className={`${(currentPage === 'landing' || currentPage === 'pricing') ? 'pt-24' : ''}`}>
        {currentPage === 'landing' && (
          <LandingPage
            onStart={handleStartFreeTrial}
            onLogin={() => setIsLoginOpen(true)}
            onNavigate={(page: string) => navigateTo(page as PageView)}
          />
        )}

        {currentPage === 'pricing' && <Pricing />}

        {currentPage === 'dashboard' && (
          <>
            <Dashboard
              key={dashboardRefreshKey}
              onOpenNewRecording={() => setIsNewRecordingOpen(true)}
              onOpenWorkspace={(taskId?: string) => {
                setSelectedTaskId(taskId || null);
                navigateTo('workspace');
              }}
              onLogout={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                sessionStorage.removeItem('tutorial-session-dismissed');
                stopHeartbeat();
                navigateTo('landing');
              }}
              onUpgrade={() => setShowUpgrade(true)}
              onOpenInvite={() => setIsInviteOpen(true)}
              onOpenShare={() => setIsShareModalOpen(true)}
            />

            {/* Guest Gate Overlay for Registration Flow */}
            {isGuestGateOpen && (
              <GuestGateModal
                onSignUp={() => setIsSignUpOpen(true)}
                onSignIn={() => setIsLoginOpen(true)}
              />
            )}

            {/* Welcome Pricing Modal after Registration */}
            {showWelcomePricing && (
              <WelcomePricingModal onClose={() => { setShowWelcomePricing(false); setDashboardRefreshKey(k => k + 1); }} />
            )}

            {/* Upgrade Modal */}
            {showUpgrade && (
              <UpgradePage onBack={() => {
                setShowUpgrade(false);
                setDashboardRefreshKey(k => k + 1);
                if (returnToNewRecording) {
                  setReturnToNewRecording(false);
                  setIsNewRecordingOpen(true);
                }
              }} />
            )}
          </>
        )}

        {currentPage === 'workspace' && (
          <Workspace
            taskId={selectedTaskId}
            onBack={() => navigateTo('dashboard')}
            onOpenShare={() => setIsShareModalOpen(true)}
          />
        )}

        {currentPage === 'public_share' && (
          <PublicShareView
            onSignUp={() => setIsSignUpOpen(true)}
            onLogin={() => setIsLoginOpen(true)}
          />
        )}

        {currentPage === 'enterprise' && <EnterprisePage onBack={() => navigateTo('landing')} />}
        {currentPage === 'api_page' && <ApiPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'about' && <AboutPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'blog' && <BlogPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'careers' && <CareersPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'contact' && <ContactPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'privacy' && <PrivacyPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'terms' && <TermsPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'security_page' && <SecurityPage onBack={() => navigateTo('landing')} />}
        {currentPage === 'changelog' && <ChangelogPage onBack={() => navigateTo('landing')} />}

      </main>

      {/* Modals */}
      {isLoginOpen && (
        <LoginModal
          onClose={() => setIsLoginOpen(false)}
          onLogin={() => { setIsLoginOpen(false); navigateTo('dashboard'); setIsGuestGateOpen(false); startHeartbeat(); }}
          onSwitchToSignUp={(prefill) => { setIsLoginOpen(false); setSignUpPrefill(prefill || null); setIsSignUpOpen(true); }}
          onForgotPassword={() => { setIsLoginOpen(false); setIsForgotPasswordOpen(true); }}
          onNavigate={(page: string) => { setIsLoginOpen(false); navigateTo(page as PageView); }}
        />
      )}

      {isSignUpOpen && (
        <SignUpModal
          onClose={() => { setIsSignUpOpen(false); setInviteCode(''); setSignUpPrefill(null); }}
          onSignUp={handleSignUpSuccess}
          onSwitchToLogin={() => { setIsSignUpOpen(false); setIsLoginOpen(true); setInviteCode(''); setSignUpPrefill(null); }}
          initialInviteCode={inviteCode}
          prefill={signUpPrefill}
        />
      )}

      {isInviteOpen && <InviteModal onClose={() => setIsInviteOpen(false)} />}

      {isNewRecordingOpen && <NewRecordingModal
        onClose={() => setIsNewRecordingOpen(false)}
        onStart={async (params) => {
          console.log('Task params:', params);
          if (!params.fileId) {
            console.error('No fileId provided');
            return;
          }

          try {
            // 调用 createDraft API（后端会检查配额并启动 pipeline）
            const { createDraft } = await import('./services/task');

            const draftResponse = await createDraft({
              fileId: params.fileId,
              industry: params.industry,
              company: params.company,
              context: params.context,
              scene: params.scene,
            });

            console.log('Task created:', draftResponse.taskId);

            // 关闭弹窗并跳转到 dashboard（任务在后台处理）
            setIsNewRecordingOpen(false);
            setSelectedTaskId(null);
            setDashboardRefreshKey(prev => prev + 1);
            navigateTo('dashboard');
          } catch (error: any) {
            console.error('Failed to create task:', error);
            if (error.message && error.message.includes('INSUFFICIENT_QUOTA')) {
              setAlertModal({
                title: language === 'en' ? 'Insufficient Quota' : '时长配额不足',
                message: language === 'en' ? 'Your quota is insufficient. Please upgrade your plan or purchase a duration package.' : '时长配额不足，请升级会员或购买时长包。',
                confirmText: language === 'en' ? 'Go Upgrade' : '去升级',
                onConfirm: () => {
                  setAlertModal(null);
                  setIsNewRecordingOpen(false);
                  setReturnToNewRecording(true);
                  setShowUpgrade(true);
                },
              });
            } else {
              setAlertModal({
                title: language === 'en' ? 'Task Creation Failed' : '创建任务失败',
                message: error.message || (language === 'en' ? 'Failed to create task' : '创建任务失败'),
              });
            }
          }
        }}
      />}

      {isShareModalOpen && (
        <ShareModal
          onClose={() => setIsShareModalOpen(false)}
          onPreviewPublicLink={() => {
            setIsShareModalOpen(false);
            navigateTo('public_share');
          }}
        />
      )}

      {isForgotPasswordOpen && (
        <ForgotPasswordModal
          onClose={() => setIsForgotPasswordOpen(false)}
          onBackToLogin={() => { setIsForgotPasswordOpen(false); setIsLoginOpen(true); }}
        />
      )}

      {/* 通用提示弹窗 */}
      {alertModal && (
        <AlertModal
          title={alertModal.title}
          message={alertModal.message}
          confirmText={alertModal.confirmText}
          onClose={() => setAlertModal(null)}
          onConfirm={alertModal.onConfirm}
        />
      )}

      {/* 踢出弹窗 */}
      {kickOutReason && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <Monitor className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-brand-900 mb-2">
              {kickOutReason === 'other_device'
                ? t.dashboard.sessionKickedTitle
                : t.dashboard.sessionExpiredTitle}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {kickOutReason === 'other_device'
                ? t.dashboard.sessionKickedMessage
                : t.dashboard.sessionExpiredMessage}
            </p>
            <button
              onClick={() => {
                setKickOutReason(null);
                resetKickOutFlag();
                navigateTo('landing');
                setIsLoginOpen(true);
              }}
              className="w-full py-3 bg-brand-900 text-white rounded-full font-semibold hover:bg-brand-800 transition-colors"
            >
              {t.dashboard.sessionConfirm}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}
